import { EllipsisOutlined } from "@ant-design/icons";
import { Button, Card, Dropdown, Modal, Table } from "antd";
import axios from "axios";
import { message } from "@bindeleddet/core/utils";
import MobileTable from "components/MobileTable/MobileTable";
import { useViewport } from "@bindeleddet/core/hooks";
import { useMemo, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { RegisteredStudentData } from "../AdminSingleEventPage";
import ManualRegistration from "./ManualRegistration";

interface Props {
  registeredStudentsData: RegisteredStudentData[];
  eventId: number;
  loading: boolean;
  onStudentDeregistered: (studentId: number) => void;
  onStudentRegistered: (studentId: number) => void;
}

const RegisteredStudents: React.FC<Props> = ({
  registeredStudentsData,
  loading,
  eventId,
  onStudentDeregistered,
  onStudentRegistered,
}) => {
  const { width } = useViewport();
  const history = useHistory();
  // TODO - fix width and padding
  // TODO - fix sorting
  // TODO - fix number logic
  const [loadingModal, setLoadingModal] = useState(false);

  const columns = [
    {
      title: "#",
      dataIndex: "number",
      key: "number",
      sorter: false,
    },
    {
      title: "Navn",
      dataIndex: "name",
      key: "name",
      render: (name: string, record: RegisteredStudentData) => {
        return <Link to={`/admin/students/${record.id}`}>{name}</Link>;
      },
      sorter: (a: RegisteredStudentData, b: RegisteredStudentData) => a.name.localeCompare(b.name),
    },
    {
      title: "Brukernavn",
      dataIndex: "username",
      key: "username",
    },
    {
      title: "Linje",
      dataIndex: "study_programme_short_name",
      key: "study_programme",
      sorter: (a: RegisteredStudentData, b: RegisteredStudentData) =>
        a.study_programme_short_name.localeCompare(b.study_programme_short_name),
    },
    {
      title: "Årstrinn",
      dataIndex: "year_level",
      key: "year_level",
      sorter: (a: RegisteredStudentData, b: RegisteredStudentData) => a.year_level - b.year_level,
    },
    {
      title: "Telefon",
      dataIndex: "mobile_number",
      key: "mobile_number",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Allergier",
      dataIndex: "allergies",
      key: "allergies",
      sorter: (a: RegisteredStudentData, b: RegisteredStudentData) => {
        const aHasAllergies = a.allergies && a.allergies.trim().length > 0;
        const bHasAllergies = b.allergies && b.allergies.trim().length > 0;

        if (aHasAllergies && !bHasAllergies) return -1;
        if (!aHasAllergies && bHasAllergies) return 1;
        return 0;
      },
    },
    {
      title: "Oppmøtt",
      dataIndex: "time_attended",
      key: "time_attended",
    },
    {
      title: "Påmeldt",
      dataIndex: "created_at",
      key: "created_at",
      sorter: (a: RegisteredStudentData, b: RegisteredStudentData) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      render: (date: string) => {
        return new Date(date).toLocaleString("nb-NO", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        });
      },
    },
    {
      key: "action",
      name: "action",
      dataIndex: "id",
      render: (studentId: number, record: RegisteredStudentData) => {
        const handleCreateTermViolation = () => {
          Modal.confirm({
            title: `Gi prikk`,
            content: `Er du sikker på at du vil gi ${record.name} en prikk?`,
            onOk: () => {
              setLoadingModal(true);
              return axios
                .post(`/events/${eventId}/create-term-violation/`, {
                  student: studentId,
                })
                .then(() => {
                  message("success", `Prikk gitt til ${record.name}`);
                })
                .catch(() => {
                  message("error", "En feil oppstod", `Prikk ble ikke gitt til ${record.name}`);
                })
                .finally(() => {
                  setLoadingModal(false);
                });
            },
            okButtonProps: {
              danger: true,
              loading: loadingModal,
            },
            okText: "Gi prikk",
            cancelText: "Avbryt",
          });
        };

        const handleDeregister = () => {
          Modal.confirm({
            title: `Meld av arrangement`,
            content: `Er du sikker på at du vil melde av ${record.name} fra arrangementet?`,
            onOk: () => {
              setLoadingModal(true);
              return axios
                .post(`/events/${eventId}/admin_unregister/`, {
                  student: studentId,
                })
                .then(() => {
                  message("success", `Studenten er meldt av arrangementet`);
                  onStudentDeregistered(studentId);
                })
                .catch(() => {
                  message("error", "En feil oppstod", "Studenten ble ikke meldt av arrangementet");
                })
                .finally(() => {
                  setLoadingModal(false);
                });
            },
            okButtonProps: {
              danger: true,
              loading: loadingModal,
            },
            okText: "Meld av",
            cancelText: "Avbryt",
          });
        };

        const menuItems = [
          { key: "1", label: "Gi prikk", onClick: handleCreateTermViolation, danger: true },
          { key: "2", label: "Meld av arrangement", onClick: handleDeregister },
        ];

        return (
          <Dropdown menu={{ items: menuItems }} trigger={["click"]}>
            <Button icon={<EllipsisOutlined />} />
          </Dropdown>
        );
      },
    },
  ];

  const alreadyRegisteredStudentIds = useMemo(() => registeredStudentsData.map(student => student.id), [
    registeredStudentsData,
  ]);

  return (
    <>
      <Card style={{ width: "100%", marginTop: "16px" }}>
        <ManualRegistration
          eventId={eventId}
          alreadyRegisteredStudentIds={alreadyRegisteredStudentIds}
          onRegistration={onStudentRegistered}
        />
      </Card>
      <Card title="Påmeldte studenter" style={{ width: "100%", marginTop: "16px" }}>
        {width > 768 ? (
          <Table
            style={{ width: "100%" }}
            dataSource={registeredStudentsData}
            columns={columns}
            pagination={false}
            loading={loading}
            locale={{ emptyText: loading ? "Laster inn påmeldte studenter..." : "Ingen påmeldte studenter" }}
          />
        ) : (
          <MobileTable
            data={registeredStudentsData}
            columns={[
              {
                title: "Navn",
                dataIndex: "name",
                key: "name",
              },
              {
                title: "Linje",
                dataIndex: "study_programme_name",
                key: "study_programme",
              },
              {
                title: "Årstrinn",
                dataIndex: "year_level",
                key: "year_level",
              },
              {
                title: "Telefon",
                dataIndex: "mobile_number",
                key: "mobile_number",
              },
              {
                title: "Email",
                dataIndex: "email",
                key: "email",
              },
              {
                title: "Allergier",
                dataIndex: "allergies",
                key: "allergies",
              },
              {
                title: "Påmeldt",
                dataIndex: "created_at",
                key: "created_at",
                render: (date: unknown) => {
                  if (!date || typeof date !== "string") return "";
                  return new Date(date).toLocaleString("nb-NO", {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  });
                },
              },
              {
                title: "Oppmøtt",
                dataIndex: "time_attended",
                key: "time_attended",
                render: (time: unknown) => (time ? String(time) : "Ikke oppmøtt"),
              },
            ]}
            loading={loading}
            onClick={id => history.push(`/admin/students/${id}/`)}
            emptyText="Ingen påmeldte studenter"
            actions={record => {
              const handleCreateTermViolation = () => {
                Modal.confirm({
                  title: `Gi prikk`,
                  content: `Er du sikker på at du vil gi ${record.name} en prikk?`,
                  onOk: () => {
                    setLoadingModal(true);
                    return axios
                      .post(`/events/${eventId}/create-term-violation/`, {
                        student: record.id,
                      })
                      .then(() => {
                        message("success", `Prikk gitt til ${record.name}`);
                      })
                      .catch(() => {
                        message("error", "En feil oppstod", `Prikk ble ikke gitt til ${record.name}`);
                      })
                      .finally(() => {
                        setLoadingModal(false);
                      });
                  },
                  okButtonProps: { danger: true, loading: loadingModal },
                  okText: "Gi prikk",
                  cancelText: "Avbryt",
                });
              };

              const handleDeregister = () => {
                Modal.confirm({
                  title: `Meld av arrangement`,
                  content: `Er du sikker på at du vil melde av ${record.name} fra arrangementet?`,
                  onOk: () => {
                    setLoadingModal(true);
                    return axios
                      .post(`/events/${eventId}/admin_unregister/`, {
                        student: record.id,
                      })
                      .then(() => {
                        message("success", `Studenten er meldt av arrangementet`);
                        onStudentDeregistered(record.id);
                      })
                      .catch(() => {
                        message("error", "En feil oppstod", "Studenten ble ikke meldt av arrangementet");
                      })
                      .finally(() => {
                        setLoadingModal(false);
                      });
                  },
                  okButtonProps: { danger: true, loading: loadingModal },
                  okText: "Meld av",
                  cancelText: "Avbryt",
                });
              };

              return (
                <Dropdown
                  menu={{
                    items: [
                      { key: "1", label: "Gi prikk", onClick: handleCreateTermViolation, danger: true },
                      { key: "2", label: "Meld av arrangement", onClick: handleDeregister },
                    ],
                  }}
                  trigger={["click"]}
                >
                  <Button icon={<EllipsisOutlined />} />
                </Dropdown>
              );
            }}
          />
        )}
      </Card>
    </>
  );
};

export default RegisteredStudents;
